import usePagination from "src/customHooks/usePagination";
import Pagination from "src/components/pagination";
import PageLoading from "src/components/pageLoading";
import { SubscriptionUser } from "../types/subscription-user.entity";
import { 
  useCountSubscriptionUsersQuery,
  useCountAllUsersWithSubscriptionsQuery,
  useGetSubscriptionUsersQuery, 
  useGetAllUsersWithSubscriptionsQuery,
  SubscriptionUsersFilter 
} from "../subscription-users.service";
import SubscriptionUsersTable from "../components/subscription-users-table";
import SubscriptionUsersSearch from "../components/subscription-users-search";
import SubscriptionUsersTableSkeleton from "../components/subscription-users-table-skeleton";
import { useCallback, useMemo } from "react";
import { FaToggleOn, FaToggleOff } from "react-icons/fa";
import { FiDownloadCloud } from "react-icons/fi";
import { useTheme } from "src/context/theme.context";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

type SubscriptionUsersRouteState = SubscriptionUsersFilter & {
  scope?: "active" | "all";
};

const SubscriptionUsersPage = () => {
  const { isDarkMode } = useTheme();
  const tableSearch = usePaginatedTableSearchParams<SubscriptionUsersRouteState>({
    parseState: (searchParams) => {
      const query: SubscriptionUsersRouteState = {};
      const subscriptionStatus = searchParams.get("subscriptionStatus");
      const subscriptionProvider = searchParams.get("subscriptionProvider");
      const languageCode = searchParams.get("languageCode");
      const scope = searchParams.get("scope");

      if (subscriptionStatus) query.subscriptionStatus = subscriptionStatus;
      if (subscriptionProvider) query.subscriptionProvider = subscriptionProvider;
      if (languageCode) query.languageCode = languageCode;
      if (scope === "all" || scope === "active") query.scope = scope;

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.subscriptionStatus) nextParams.set("subscriptionStatus", state.subscriptionStatus);
      if (state.subscriptionProvider) nextParams.set("subscriptionProvider", state.subscriptionProvider);
      if (state.languageCode) nextParams.set("languageCode", state.languageCode);
      nextParams.set("scope", state.scope === "all" ? "all" : "active");
      return nextParams;
    },
  });

  const initialRouteState = tableSearch.state;
  const showAllUsers = initialRouteState.scope === "all";
  const initialQuery = useMemo<SubscriptionUsersFilter>(
    () => ({
      ...(initialRouteState.subscriptionStatus ? { subscriptionStatus: initialRouteState.subscriptionStatus } : {}),
      ...(initialRouteState.subscriptionProvider ? { subscriptionProvider: initialRouteState.subscriptionProvider } : {}),
      ...(initialRouteState.languageCode ? { languageCode: initialRouteState.languageCode } : {}),
    }),
    [initialRouteState.languageCode, initialRouteState.subscriptionProvider, initialRouteState.subscriptionStatus]
  );
  const paginationSyncKey = `${tableSearch.syncKey}::${JSON.stringify(initialQuery)}`;
  // Choose which hooks to use based on toggle state
  const activeUsersPagination = usePagination<SubscriptionUser & { id: string; __cursor?: string }>({
    useGetDataQuery: useGetSubscriptionUsersQuery,
    useCountData: useCountSubscriptionUsersQuery,
    initQuery: initialQuery,
    initPageTokens: tableSearch.pageTokens,
    initCurrentIndex: tableSearch.currentIndex,
    syncKey: `${paginationSyncKey}::active`,
    onPaginationStateChange: tableSearch.replacePagination,
    getCursorId: (u: any) => u.__cursor ?? u.id,
    skip: showAllUsers,
  });

  const allUsersPagination = usePagination<SubscriptionUser & { id: string; __cursor?: string }>({
    useGetDataQuery: useGetAllUsersWithSubscriptionsQuery,
    useCountData: useCountAllUsersWithSubscriptionsQuery,
    initQuery: initialQuery,
    initPageTokens: tableSearch.pageTokens,
    initCurrentIndex: tableSearch.currentIndex,
    syncKey: `${paginationSyncKey}::all`,
    onPaginationStateChange: tableSearch.replacePagination,
    getCursorId: (u: any) => u.__cursor ?? u.id,
    skip: !showAllUsers,
  });

  // Use the appropriate pagination based on toggle state
  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching, totalCount, isCountLoading } = 
    showAllUsers ? allUsersPagination : activeUsersPagination;

  const updateQuery = useCallback((newQuery: SubscriptionUsersFilter) => {
    tableSearch.replaceFilters({
      ...newQuery,
      scope: showAllUsers ? "all" : "active",
    });
  }, [showAllUsers, tableSearch]);

  const toggleShowAllUsers = useCallback(() => {
    tableSearch.replaceFilters({
      ...initialQuery,
      scope: showAllUsers ? "active" : "all",
    });
  }, [initialQuery, showAllUsers, tableSearch]);

  const showTableSkeleton = !data && (isLoading || isFetching);
  const countLabel = showAllUsers ? "subscription users" : "active subscriptions";

  return (
    <div className="page_container">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-main-color-text">Subscription Users</h1>
          <div
            className={`inline-flex min-h-[44px] items-center gap-3 rounded-full border px-3 py-2 text-left transition-all duration-200 ${
              isDarkMode
                ? "border-emerald-400/25 bg-slate-800/95 text-emerald-50 shadow-[0_12px_30px_rgba(16,185,129,0.12)]"
                : "border-emerald-200 bg-emerald-50/80 text-emerald-950"
            }`}
          >
            {isCountLoading ? (
              <div className="space-y-1">
                <div className={`h-2.5 w-16 rounded animate-pulse ${isDarkMode ? "bg-emerald-300/20" : "bg-emerald-100"}`} />
                <div className={`h-4 w-10 rounded animate-pulse ${isDarkMode ? "bg-emerald-300/30" : "bg-emerald-200"}`} />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <FiDownloadCloud className={`text-base ${isDarkMode ? "text-emerald-300" : "text-emerald-700"}`} />
                <div className="flex items-baseline gap-2">
                  <span className={`text-lg font-bold tabular-nums tracking-[-0.02em] ${isDarkMode ? "text-emerald-50" : "text-emerald-950"}`}>
                    {totalCount ?? 0}
                  </span>
                  <span className={`text-xs font-semibold ${isDarkMode ? "text-emerald-200" : "text-emerald-800"}`}>
                    {countLabel}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={toggleShowAllUsers}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 ${
            showAllUsers
              ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
              : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
          } ${
            isDarkMode
              ? showAllUsers
                ? "bg-emerald-500/15 text-emerald-100 border-emerald-500/30 hover:bg-emerald-500/20"
                : "bg-sky-500/15 text-sky-100 border-sky-500/30 hover:bg-sky-500/20"
              : ""
          }`}
        >
          {showAllUsers ? (
            <>
              <FaToggleOn className="text-lg" />
              <span>Show All Users with Previous Subscriptions</span>
            </>
          ) : (
            <>
              <FaToggleOff className="text-lg" />
              <span>Show Only Active Subscription Users</span>
            </>
          )}
        </button>
      </div>
      <div className="mb-4">
        <SubscriptionUsersSearch updateQuery={updateQuery} initialQuery={initialQuery} isSearching={isLoading || isFetching} />
      </div>
      {showTableSkeleton ? (
        <>
          <SubscriptionUsersTableSkeleton rows={10} />
          <Pagination numberOfPages={numberOfPages ?? 1} currentPage={currentPage} prev={prev} next={next} isLoading />
        </>
      ) : isSuccess ? (
        <>
          <SubscriptionUsersTable data={data ?? []} />
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <div
          className={`rounded-lg border p-6 text-center ${
            isDarkMode ? "border-red-500/30 bg-red-500/10" : "border-red-200 bg-red-50"
          }`}
        >
          <h3 className={`mb-2 text-lg font-semibold ${isDarkMode ? "text-red-200" : "text-red-800"}`}>Error Loading Subscription Users</h3>
          <p className={isDarkMode ? "text-red-300" : "text-red-600"}>
            There was an error fetching the subscription users data. Please check the console for more details.
          </p>
        </div>
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default SubscriptionUsersPage;
